import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from './AuthContext';

const VerifyEmail = () => {
    const { currentUser, sendVerificationEmail, logout } = useAuth();
    const navigate = useNavigate();

    const [resending, setResending] = useState(false);
    const [resendSuccess, setResendSuccess] = useState(false);
    const [resendError, setResendError] = useState('');
    const [cooldown, setCooldown] = useState(0);
    const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // Anonyme Nutzer werden sofort weitergeleitet
    useEffect(() => {
        if (currentUser?.isAnonymous) {
            navigate('/lernsets');
        }
    }, [currentUser, navigate]);

    useEffect(() => {
        if (cooldown <= 0) return;
        cooldownRef.current = setInterval(() => {
            setCooldown(c => {
                if (c <= 1) { clearInterval(cooldownRef.current!); return 0; }
                return c - 1;
            });
        }, 1000);
        return () => clearInterval(cooldownRef.current!);
    }, [cooldown > 0]);

    useEffect(() => {
        if (!currentUser) return;
        const poller = setInterval(async () => {
            await currentUser.reload();
            if (currentUser.emailVerified) {
                clearInterval(poller);
                navigate('/lernsets');
            }
        }, 3000);
        return () => clearInterval(poller);
    }, [currentUser, navigate]);

    useEffect(() => {
        if (currentUser?.emailVerified) navigate('/lernsets');
    }, [currentUser, navigate]);

    useEffect(() => {
        if (!currentUser) navigate('/login');
    }, [currentUser, navigate]);

    const handleResend = async () => {
        if (!currentUser || cooldown > 0) return;
        setResending(true);
        setResendError('');
        setResendSuccess(false);
        try {
            await sendVerificationEmail(currentUser);
            setResendSuccess(true);
            setCooldown(60);
        } catch (err: any) {
            if (err.code === 'auth/too-many-requests') {
                setResendError('Zu viele Anfragen. Bitte warte ein paar Minuten.');
            } else {
                setResendError('Fehler beim Senden. Bitte versuche es später erneut.');
            }
        } finally {
            setResending(false);
        }
    };

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    if (!currentUser) return null;
    if (currentUser.isAnonymous) return null;

    return (
        <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-indigo-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none">
                <motion.div
                    animate={{ scale: [1, 1.12, 1], opacity: [0.13, 0.2, 0.13] }}
                    transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
                    className="absolute -top-40 -right-40 w-96 h-96 bg-indigo-300 rounded-full mix-blend-multiply filter blur-3xl"
                />
                <motion.div
                    animate={{ scale: [1, 1.08, 1], opacity: [0.1, 0.18, 0.1] }}
                    transition={{ duration: 11, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
                    className="absolute -bottom-40 -left-40 w-96 h-96 bg-purple-300 rounded-full mix-blend-multiply filter blur-3xl"
                />
            </div>

            <motion.div
                initial={{ opacity: 0, y: 28 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
                className="w-full max-w-md"
            >
                <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/60 overflow-hidden">

                    <div className="bg-gradient-to-br from-indigo-600 to-purple-600 px-8 pt-10 pb-12 text-center relative overflow-hidden">
                        {[0,1,2].map(i => (
                            <motion.div
                                key={i}
                                className="absolute text-white/10 text-5xl select-none pointer-events-none"
                                style={{ left: `${20 + i*30}%`, top: '10px' }}
                                animate={{ y: [-5, -30], opacity: [0.4, 0] }}
                                transition={{ duration: 2.5, repeat: Infinity, delay: i * 0.8, ease: 'easeOut' }}
                            >
                                ✉
                            </motion.div>
                        ))}

                        <motion.div
                            initial={{ scale: 0.6, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ delay: 0.15, type: 'spring', stiffness: 200 }}
                            className="relative inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm border border-white/30 mb-5"
                        >
                            <span className="text-4xl">📬</span>
                        </motion.div>

                        <h1 className="text-2xl font-bold text-white mb-1">E-Mail bestätigen</h1>
                        <p className="text-indigo-100 text-sm">Fast geschafft — noch ein Klick</p>
                    </div>

                    <div className="relative -mt-6 mx-6 bg-white rounded-2xl shadow-lg border border-gray-100 px-6 py-5 mb-6">
                        <p className="text-gray-700 text-sm leading-relaxed text-center">
                            Wir haben einen Bestätigungs-Link an{' '}
                            <span className="font-semibold text-indigo-600 break-all">
                                {currentUser.email || 'keine E-Mail'}
                            </span>{' '}
                            gesendet.
                        </p>

                        <div className="flex items-center justify-center gap-3 mt-4">
                            {[
                                { icon: '📥', label: 'E-Mail öffnen' },
                                { icon: '→', label: '', arrow: true },
                                { icon: '🔗', label: 'Link klicken' },
                                { icon: '→', label: '', arrow: true },
                                { icon: '🎉', label: 'Fertig!' },
                            ].map((s, i) =>
                                s.arrow ? (
                                    <span key={i} className="text-gray-300 font-bold text-lg">→</span>
                                ) : (
                                    <motion.div
                                        key={i}
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.3 + (i / 2) * 0.1 }}
                                        className="flex flex-col items-center gap-1"
                                    >
                                        <div className="w-11 h-11 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-lg shadow-sm">
                                            {s.icon}
                                        </div>
                                        <span className="text-[11px] text-gray-500 leading-tight text-center max-w-[56px]">
                                            {s.label}
                                        </span>
                                    </motion.div>
                                )
                            )}
                        </div>
                    </div>

                    <div className="px-8 pb-8 space-y-4">
                        <div className="flex items-center justify-center gap-2 text-xs text-gray-400 bg-gray-50 rounded-xl py-2.5 border border-gray-100">
                            {[0, 1, 2].map(i => (
                                <motion.div
                                    key={i}
                                    animate={{ scale: [1, 1.5, 1], opacity: [0.4, 1, 0.4] }}
                                    transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.2 }}
                                    className="w-1.5 h-1.5 rounded-full bg-indigo-400"
                                />
                            ))}
                            <span>Warte auf deine Bestätigung…</span>
                        </div>

                        <AnimatePresence mode="wait">
                            {resendSuccess && (
                                <motion.div
                                    key="ok"
                                    initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                                    className="text-sm text-center bg-green-50 border border-green-200 text-green-700 rounded-xl py-2.5 px-4"
                                >
                                    ✅ E-Mail wurde erneut gesendet!
                                </motion.div>
                            )}
                            {resendError && (
                                <motion.div
                                    key="err"
                                    initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                                    className="text-sm text-center bg-red-50 border border-red-200 text-red-600 rounded-xl py-2.5 px-4"
                                >
                                    {resendError}
                                </motion.div>
                            )}
                        </AnimatePresence>

                        <button
                            onClick={handleResend}
                            disabled={resending || cooldown > 0}
                            className="w-full py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
                        >
                            {resending ? (
                                <>
                                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                                    </svg>
                                    Wird gesendet…
                                </>
                            ) : cooldown > 0 ? (
                                `Erneut senden in ${cooldown}s`
                            ) : (
                                '📨 E-Mail erneut senden'
                            )}
                        </button>

                        <button
                            onClick={handleLogout}
                            className="w-full py-3 border border-gray-200 text-gray-500 rounded-xl font-medium hover:bg-gray-50 transition-colors text-sm"
                        >
                            Abmelden
                        </button>

                        <p className="text-[11px] text-gray-400 text-center leading-relaxed pt-1">
                            Keine E-Mail erhalten? Prüfe auch deinen Spam-Ordner.
                            Sollte das Problem weiter bestehen, melde dich ab und registriere dich erneut.
                        </p>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export default VerifyEmail;